<template>
  <IonPage>
    <MapModalHeader>
      <GatewayToolbar></GatewayToolbar>
    </MapModalHeader>
    <IonContent color="white">
      <IonRefresher :mode="refresherMode" slot="fixed" @ion-refresh="IonRefresh">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div class="p-6">
        <Description
          :text="description.text"
          :created-at="description.createdAt"
          :created-by="description.createdBy"
          :on-save="saveDescription"
        />

        <!-- Gateway Img Section -->
        <div class="relative">
          <div
            class="absolute left-0 right-0 top-4 flex items-center justify-center text-lg text-gray-700 font-medium mt-4"
          >
            <i class="icon-barcode mr-2 text-gray-500"></i>
            <span>{{ gatewayIdStr }}</span>
          </div>
          <DeviceImg class="mx-auto w-80 relative" :device="selectedProperty.property?.gateway" />
          <div class="absolute left-0 right-0 bottom-2">
            <div v-if="!selectedProperty.property?.gateway?.id">
              <LightBlockButton>
                <div class="flex items-center">
                  <i class="icon-barcode mr-2 text-blue-500"></i>
                  Program Gateway
                </div>
              </LightBlockButton>
            </div>
            <DeviceOnlineStatus
              @on-sync-complete="selectedProperty.refresh()"
              v-else
              :device="selectedProperty.property?.gateway"
            />
          </div>
        </div>
      </div>

      <GatewayNetworkStatus :status="gatewayStatus" class="mb-6" />

      <div class="transition-colors duration-200" :class="{ 'bg-gray-100': showNetworkDetails }">
        <div
          class="mx-6 rounded-md bg-gray-100 text-xs text-gray-500 relative"
          @click="showNetworkDetails = !showNetworkDetails"
        >
          <BaseClearButton color="light" size="small" class="p-1 h-full w-full">
            <i class="icon-settings text-blue-500 mr-2 text-base"></i>
            <span class="text-gray-700 text-sm">Network Details</span>
          </BaseClearButton>
          <div
            class="h-0.5 bg-gray-300 rounded-full absolute left-0 bottom-0 right-0 transition-transform scale-x-0 duration-200"
            :class="{ 'scale-x-100': showNetworkDetails }"
          ></div>
          <div class="absolute right-3 top-0 bottom-0 flex items-center">
            <i
              class="icon-chevron-right text-lg text-gray-700 transition-transform duration-200"
              :class="{ 'rotate-90': showNetworkDetails }"
            ></i>
          </div>
        </div>
        <Collapse :when="showNetworkDetails">
          <div class="p-2">
            <GatewayNetworkDetails
              :status="gatewayStatus"
              :cellular-enabled="selectedProperty.property?.subscriptions.CELLULAR.enabled"
              :cellular-cost="cellularCost"
              :disable-cellular-handler="disableCellular"
              :enable-cellular-handler="enableCellular"
            />
            <div class="mt-8 mb-4">
              <BaseClearButton @click="showNetworkDetails = false" size="small" class="w-full h-full">
                <i class="icon-eye-off text-base mr-2"></i>
                Hide Network Details
              </BaseClearButton>
            </div>
          </div>
        </Collapse>
      </div>

      <div class="p-6">
        <GatewaySyncHistory
          :property-id="selectedProperty.property?._id"
          :gateway-id="selectedProperty.property?.gateway?.id"
        />
        <AdditionalInfo
          class="py-12"
          :photo="photo"
          :allow-photos="true"
          context="GATEWAY"
          :on-upload-photo="uploadPhoto"
          :comments="selectedProperty.property?.gateway?.comments"
          :on-refresh="IonRefresh"
          :on-add-comment="selectedProperty.addGatewayComment"
          :on-remove-comment="selectedProperty.removeGatewayComment"
        />
        <div class="mb-4 text-gray-500 font-medium text-sm text-center">
          <p>
            The equipment on this property is configured to check-in every
            <span class="text-gray-700">{{ dataResolutionString }}. </span>
            <span v-if="electricDataResolutionString !== dataResolutionString">
              Electric Meter Devices are configured to check-in every
              <span class="text-gray-700">{{ electricDataResolutionString }}.</span>
            </span>
          </p>
        </div>
        <SectionTitle>Quick Actions</SectionTitle>
        <div v-if="!selectedProperty.property?.gateway?.id">
          <QuickActionButton
            icon="icon-barcode"
            title="Program Gateway"
            :subtitle="`Program a Gateway for ${selectedProperty.property?.name}`"
          />
        </div>
        <div v-else>
          <QuickActionButton
            icon="icon-map-pin"
            title="Drop a Pin"
            subtitle="Save your current location as this Gateway's location"
            @click="dropPin()"
          />
          <ReplaceGatewayAction
            ref="replaceGatewayAction"
            v-if="selectedProperty.property?.gateway?.id"
            :gateway-id="selectedProperty.property.gateway.id"
          ></ReplaceGatewayAction>
          <QuickActionButton
            color="red"
            icon="icon-trash"
            title="Remove Gateway"
            :subtitle="`Delete ${gatewayIdStr} from ${selectedProperty.property.name}`"
            @click="removeGateway()"
          />
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { useSelectedProperty } from '@/stores/selected-property.store';
import {
  alertController,
  createAnimation,
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  loadingController,
  onIonViewDidLeave,
  onIonViewWillEnter,
  useIonRouter,
} from '@ionic/vue';
import { computed, ref } from 'vue';
import { GW3Read, GW4Read, UtilityTypeIds } from '@ncss/models/dist/v3';
import DeviceImg from '../components/DeviceImg.vue';
import DeviceOnlineStatus from '../components/DeviceOnlineStatus.vue';
import LightBlockButton from '@/components/forms/LightBlockButton.vue';
import BaseClearButton from '@/components/forms/BaseClearButton.vue';
import GatewaySyncHistory from './GatewaySyncHistory.vue';
import SectionTitle from '@/components/forms/SectionTitle.vue';
import QuickActionButton from '@/components/forms/QuickActionButton.vue';
import AdditionalInfo from '../components/AdditionalInfo.vue';
import Description from '../components/Description.vue';
import { getUrlForPhoto } from '@/components/photos/photoUtils';
import { useToast } from '@/composables/use-toast';
import { Collapse } from 'vue-collapsed';
import GatewayNetworkStatus from './GatewayNetworkStatus.vue';
import GatewayNetworkDetails from './GatewayNetworkDetails.vue';
import { useCellCost } from '@/views/tools/direct-connect/composables/use-cell-cost';
import ReplaceGatewayAction from './ReplaceGatewayAction.vue';
import { useLocation } from '@/stores/location.store';
import { useRefresherMode } from '@/composables/use-refresher-mode';
import GatewayToolbar from '../toolbars/GatewayToolbar.vue';
import MapModalHeader from '../MapModalHeader.vue';
import { ConversionUtils } from '@ncss/models';

const showNetworkDetails = ref(false);
const { successToast, errorToast } = useToast();
const selectedProperty = useSelectedProperty();
const { cellularCost } = useCellCost(() => selectedProperty.property?._id);
const router = useIonRouter();
const gatewayIdStr = computed(() => {
  if (!selectedProperty.property?.gateway?.id) {
    return '';
  }
  return ConversionUtils.ConvertSerialNumberToString(selectedProperty.property.gateway.id);
});
const { mode: refresherMode } = useRefresherMode();
const location = useLocation();
const gatewayStatus = computed(() => {
  const data = selectedProperty.property?.gateway?.data;
  return {
    ...selectedProperty.property?.gateway?.computeGatewayStatus(),
    ethernetUp: data?.ethUp(),
    ethernetValidIP: data?.ethValidIP(),
    wifiUp: data?.wlanUp(),
    wifiValidIP: data?.wlanValidIP(),
    cellularCloudConnected:
      data && (data instanceof GW3Read || data instanceof GW4Read) ? data?.cellularCloudConnected() : undefined,
  };
});

const description = computed(() => {
  return {
    text: selectedProperty.property?.gateway?.description,
    createdAt: selectedProperty.property?.gateway?.descriptionChangedOn,
    createdBy: selectedProperty.property?.gateway?.descriptionChangedBy,
  };
});

const photo = computed(() => {
  const latestPicture = selectedProperty.property?.gateway?.docs?.findLast((d) => /image/.test(d.type));
  if (!latestPicture || !selectedProperty.property?._id) {
    return;
  }
  return {
    ...latestPicture,
    url: latestPicture.url || getUrlForPhoto(selectedProperty.property?._id, latestPicture.name),
  };
});

const dataResolution = computed(() => selectedProperty.property?.settings?.dataResolution || {});
const electricDataResolutionHours = computed(() => (dataResolution.value[UtilityTypeIds.ELECTRIC] || 360) / 60);
const dataResolutionHours = computed(() => {
  let resolutionMinutes = 360;
  const resolution = dataResolution.value;
  Object.keys(resolution).forEach((ut) => {
    if (+ut !== UtilityTypeIds.ELECTRIC && resolution[+ut] !== 360) {
      resolutionMinutes = resolution[+ut];
    }
  });
  return resolutionMinutes / 60;
});

const dataResolutionString = computed(() => resolutionHoursToString(dataResolutionHours.value));
const electricDataResolutionString = computed(() => resolutionHoursToString(electricDataResolutionHours.value));

function resolutionHoursToString(hours: number) {
  if (hours === 1) {
    return `${hours} hour`;
  } else if (hours < 1) {
    return `${hours * 60} minutes`;
  } else {
    return `${hours} hours`;
  }
}

async function IonRefresh(e?: CustomEvent) {
  await selectedProperty.refresh();
  (e?.target as any)?.complete();
}

async function saveDescription(description: string) {
  await selectedProperty.updateGatewayDescription(description);
  return { success: true };
}

async function uploadPhoto(base64?: string) {
  if (!base64) {
    return;
  }
  await selectedProperty.addGatewayPhoto(base64);
  successToast('Photo saved!');
}

function enableCellular(): Promise<{ success: boolean; error?: string }> {
  return selectedProperty.setCellularEnabled(true);
}

function disableCellular(): Promise<{ success: boolean; error?: string }> {
  return selectedProperty.setCellularEnabled(false);
}

async function dropPin() {
  const l = await loadingController.create({ message: 'Saving location...' });
  await l.present();
  const coords = await location.getCoordinates();
  if (coords) {
    const { success, error } = await selectedProperty.saveGatewayLocation({
      lat: coords[1],
      long: coords[0],
    });
    await l.dismiss();
    if (success) {
      successToast('Gateway location saved!');
    } else if (error) {
      errorToast(error);
    }
  } else {
    await l.dismiss();
    errorToast('Unable to retrieve current location');
  }
}

async function removeGateway() {
  const a = await alertController.create({
    header: 'Are You Sure?',
    message: 'Are you sure you want to remove the Gateway from this property?',
    buttons: [
      {
        role: 'cancel',
        text: 'Cancel',
      },
      {
        role: 'destructive',
        text: 'Remove Gateway',
      },
    ],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role !== 'destructive') {
    return;
  }
  const { success, error } = await selectedProperty.removeGateway();
  if (success) {
    successToast('Gateway removed!');
    if (selectedProperty.property) {
      router.replace(`/property/${selectedProperty.property._id}`, () => createAnimation());
    }
  } else if (error) {
    errorToast(error);
  }
}
</script>

<style scoped>
.bg-white-enter-active {
  transition: background-color 300ms ease-out;
}
</style>
